import HeartDark from '@propertypal/shared/src/resources/icons/property/favourites-icon.svg';
import HeartSolid from '@propertypal/shared/src/resources/icons/property/favourites-orange.svg';
import Heart from '@propertypal/shared/src/resources/icons/property/favourites-white.svg';
import React, { CSSProperties, FunctionComponent, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { FavouriteBox, Item } from './PropertyFavourite.style';

interface Props {
  containerStyle?: CSSProperties;
  favourited: boolean;
  onFavourite: (favourited: boolean) => Promise<boolean>;
  dark?: boolean;
  innerRef?: React.RefObject<HTMLButtonElement>;
}

const PropertyFavourite: FunctionComponent<Props> = (props) => {
  const mounted = useRef<boolean>(false);
  const theme = useTheme();
  const [playState, setPlayState] = useState<'running' | 'paused'>('paused');
  const [favourited, setFavourited] = useState<boolean>(props.favourited);

  const handleClick: MouseEventHandler = async (e) => {
    e.preventDefault();
    setFavourited(!favourited);
    setPlayState('running');

    setTimeout(() => {
      // handle preventing updating unmounted component
      // when unfavouriting a property that removes it from a list
      // this timeout may not have completed by the time this component was unmounted
      if (mounted.current) setPlayState('paused');
    }, 1000);

    const loggedIn = await props.onFavourite(!favourited);

    if (!loggedIn) {
      setFavourited(false);
    }
  };

  useEffect(() => {
    if (props.favourited !== favourited) {
      setFavourited(props.favourited);
    }
  }, [props.favourited]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <FavouriteBox
      ref={props.innerRef}
      style={props.containerStyle}
      playState={playState}
      onClick={handleClick}
      tabIndex={-1}
      data-testid="property-favourite-button"
      type="button"
      aria-label="Favourite property"
    >
      <Item opacity={1}>
        {props.dark && (
          <HeartDark
            viewBox="0 0 33 30"
            color={theme.textDark}
            style={{ width: 28, height: 25, marginLeft: 1, marginBottom: 1 }}
            className="pp-favourite-icon"
          />
        )}
        {!props.dark && <Heart />}
      </Item>

      <Item opacity={favourited ? 1 : 0} data-testid="favourite-icon">
        <HeartSolid />
      </Item>
    </FavouriteBox>
  );
};

export default PropertyFavourite;
